import { IdentitySecurityControls } from "../../../../shared/IdentitySecurityControls";
import React, { useState } from "react";
import { ICON_PATHS } from "../../config/icons";
import type { MetricSpec, RoleProfile, WorkspaceRow, WorkspaceSpec } from "../../types/domain";

export function Icon({
  name,
  size = 18,
  className = "",
}: {
  name: string;
  size?: number;
  className?: string;
}) {
  const d = ICON_PATHS[name] || ICON_PATHS.grid;
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}
function statusTone(value: string) {
  const v = value.toLowerCase();
  if (
    v.includes("urgent") ||
    v.includes("high") ||
    v.includes("blocked") ||
    v.includes("risk") ||
    v.includes("revoked")
  )
    return "danger";
  if (
    v.includes("due") ||
    v.includes("draft") ||
    v.includes("review") ||
    v.includes("attention") ||
    v.includes("pending") ||
    v.includes("restricted")
  )
    return "warn";
  if (
    v.includes("active") ||
    v.includes("ready") ||
    v.includes("healthy") ||
    v.includes("track") ||
    v.includes("approved") ||
    v.includes("connected") ||
    v.includes("valid")
  )
    return "good";
  return "info";
}
export function Status({ children }: { children: React.ReactNode }) {
  return <span className={`status status-${statusTone(String(children))}`}>{children}</span>;
}
function Progress({ value, green = false }: { value: number; green?: boolean }) {
  return (
    <div className={`progress ${green ? "progress-green" : ""}`} aria-label={`${value}%`}>
      <span style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
    </div>
  );
}

function metricPercent(value: string, fallback: number) {
  const parsed = Number.parseInt(value, 10);
  return Math.max(0, Math.min(100, Number.isFinite(parsed) ? parsed : fallback));
}
function PageHero({
  kicker,
  title,
  description,
  children,
}: {
  kicker: string;
  title: string;
  description: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="page-hero">
      <div className="page-hero-copy">
        <div className="page-kicker">{kicker}</div>
        <h2>{title}</h2>
        <p>{description}</p>
      </div>
      {children ? <div className="hero-actions">{children}</div> : null}
    </div>
  );
}
export function Card({
  title,
  subtitle,
  actions,
  children,
  className = "",
}: {
  title?: string;
  subtitle?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={`card ${className}`}>
      {title || actions ? (
        <div className="card-header">
          <div className="card-title">
            {title ? <h3>{title}</h3> : null}
            {subtitle ? <p>{subtitle}</p> : null}
          </div>
          {actions ? <div className="card-actions">{actions}</div> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}
function Metric({
  icon,
  value,
  label,
  change,
}: {
  icon: string;
  value: string;
  label: string;
  change?: string;
}) {
  return (
    <div className="metric-card">
      <div className="metric-head">
        <div className="metric-icon">
          <Icon name={icon} size={17} />
        </div>
        {change ? <span className="metric-change">{change}</span> : null}
      </div>
      <div className="metric-value">{value}</div>
      <div className="metric-label">{label}</div>
    </div>
  );
}
export function Button({
  children,
  onClick,
  icon,
  variant = "",
  small = false,
  type = "button",
  disabled = false,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  icon?: string;
  variant?: string;
  small?: boolean;
  type?: "button" | "submit";
  disabled?: boolean;
}) {
  return (
    <button
      type={type}
      className={`btn ${variant ? `btn-${variant}` : ""} ${small ? "btn-sm" : ""}`}
      onClick={onClick}
      disabled={disabled}
    >
      {icon ? <Icon name={icon} size={small ? 14 : 16} /> : null}
      <span>{children}</span>
    </button>
  );
}
export function FilterToolbar({
  query,
  setQuery,
  status,
  setStatus,
  spec,
  onExport,
  onSavedView,
}: {
  query: string;
  setQuery: (v: string) => void;
  status: string;
  setStatus: (v: string) => void;
  spec: WorkspaceSpec;
  onExport: () => void;
  onSavedView: () => void;
}) {
  const statuses = Array.from(new Set(spec.rows.map(r => String(r.status || "Active"))));
  return (
    <div className="workspace-toolbar">
      <div className="input-wrap">
        <Icon name="search" size={14} />
        <input
          className="input"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder={`Search ${spec.title.toLowerCase()}…`}
        />
      </div>
      <select
        className="select"
        value={status}
        onChange={e => setStatus(e.target.value)}
        aria-label={`Filter ${spec.title} by status`}
      >
        <option value="All">All statuses</option>
        {statuses.map(s => (
          <option key={s}>{s}</option>
        ))}
      </select>
      <Button small icon="bookmark" onClick={onSavedView}>
        Saved view
      </Button>
      <Button small icon="download" onClick={onExport}>
        Export
      </Button>
    </div>
  );
}

export function Metrics({ items }: { items: MetricSpec[] }) {
  return (
    <div className="metrics-grid">
      {items.map(item => (
        <Metric key={item.label} {...item} change={item.trend} />
      ))}
    </div>
  );
}

function RecordsTable({
  spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  const render = (row: WorkspaceRow, key: string) => {
    const value = row[key];
    if (key === "status") return <Status>{String(value)}</Status>;
    if (key === "progress")
      return (
        <div className="cell-progress">
          <Progress value={Number(value)} green />
          <small>{value}%</small>
        </div>
      );
    if (key === "evidence")
      return (
        <div className="cell-progress">
          <Progress value={parseInt(String(value))} green />
          <small>{value}</small>
        </div>
      );
    return <>{String(value ?? "—")}</>;
  };
  return (
    <div className="v2-table-wrap">
      <table className="v2-table">
        <thead>
          <tr>
            {spec.columns.map(c => (
              <th key={c.key}>{c.label}</th>
            ))}
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={String(row.id || i)} onClick={() => onOpen(row)}>
              {spec.columns.map((c, index) => (
                <td key={c.key}>
                  {index === 0 ? (
                    <>
                      <b>{String(row[c.key] ?? row.name ?? "Record")}</b>
                      <span>{String(row.id || "")}</span>
                    </>
                  ) : (
                    render(row, c.key)
                  )}
                </td>
              ))}
              <td>
                <Button small icon="arrow" onClick={() => onOpen(row)}>
                  Open
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EntityCards({
  spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  return (
    <div className="v2-card-grid">
      {rows.map((row, i) => (
        <button
          type="button"
          className="entity-card-v2"
          key={String(row.id || i)}
          onClick={() => onOpen(row)}
        >
          <div className="entity-card-top">
            <span className="entity-card-icon">
              <Icon name={i % 2 ? spec.icon : "layers"} />
            </span>
            <span className="entity-card-copy">
              <b>{String(row.name)}</b>
              <span>
                {String(row.scope)} · {String(row.owner)}
              </span>
            </span>
            <Status>{String(row.status)}</Status>
          </div>
          <div className="entity-card-meta">
            <div>
              <span>People / units</span>
              <b>{String(row.people)}</b>
            </div>
            <div>
              <span>Progress</span>
              <b>{Number.isFinite(Number(row.progress)) ? `${row.progress}%` : "Not assessed"}</b>
            </div>
          </div>
          <div className="entity-card-foot">
            {Number.isFinite(Number(row.progress)) ? (
              <Progress value={Number(row.progress)} green />
            ) : (
              <span>Not assessed</span>
            )}
            <Icon name="arrow" size={14} />
          </div>
        </button>
      ))}
    </div>
  );
}

function MiniChart({ values }: { values: number[] }) {
  return (
    <div className="chart-shell">
      <div className="chart-columns-v2">
        {values.map((value, i) => (
          <div className="chart-column-v2" key={i} style={{ height: `${value}%` }}>
            <span>{value}%</span>
            <i>{["W1", "W2", "W3", "W4", "W5", "W6", "W7", "W8"][i]}</i>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Notes({ spec }: { spec: WorkspaceSpec }) {
  return (
    <div className="policy-list">
      {spec.notes.map((note, i) => (
        <div className="policy-item" key={note.title}>
          <Icon name={i === 0 ? "shield" : i === 1 ? "link" : "database"} size={15} />
          <span>
            <strong>{note.title}</strong>
            <span>{note.body}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

export function WorkspaceHero({
  spec,
  canManage,
  onPrimary,
  onExport,
}: {
  spec: WorkspaceSpec;
  canManage: boolean;
  onPrimary: () => void;
  onExport: () => void;
}) {
  return (
    <PageHero kicker={`${spec.areaLabel} · ${spec.group}`} title={spec.title} description={spec.description}>
      <Button icon="plus" variant="primary" onClick={onPrimary} disabled={!canManage}>
        {spec.primaryAction}
      </Button>
      <Button icon="download" onClick={onExport}>
        Export
      </Button>
    </PageHero>
  );
}

export function AccessBoundary({
  spec,
  role,
  onSwitchRole,
  onRequestAccess,
}: {
  spec: WorkspaceSpec;
  role: RoleProfile;
  onSwitchRole: () => void;
  onRequestAccess: () => void;
}) {
  return (
    <div className="access-boundary">
      <span className="big-icon">
        <Icon name="lock" size={28} />
      </span>
      <h2>This role does not have access to {spec.title}.</h2>
      <p>
        {role.name} is scoped to {role.scope}. Access is enforced by role, delegated institution scope,
        purpose, data sensitivity, approval state and expiry—not merely by hiding navigation.
      </p>
      <div className="hero-actions">
        <Button icon="users" variant="primary" onClick={onSwitchRole}>
          Preview another institution role
        </Button>
        <Button icon="help" onClick={onRequestAccess}>
          Request governed access
        </Button>
      </div>
    </div>
  );
}

function DashboardLayout({
  spec,
  rows,
  onOpen,
  onNavigate,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
  onNavigate: (area: string, page: string) => void;
}) {
  return (
    <>
      <div className="v2-grid">
        <div className="v2-stack">
          <Card
            title="Institutional momentum"
            subtitle="Eight-week composite of academic delivery, student success, quality, capacity and connected-platform activity."
          >
            <MiniChart values={spec.chart} />
          </Card>
          <Card title="Priority work" subtitle="Named owners and governed next actions.">
            <div className="list">
              {rows.map(row => (
                <button
                  type="button"
                  className="list-row"
                  key={String(row.id)}
                  onClick={() => onOpen(row)}
                  style={{ width: "100%", textAlign: "left", color: "inherit" }}
                >
                  <span className="list-icon">
                    <Icon name={spec.icon} />
                  </span>
                  <span className="list-copy">
                    <b>{String(row.name)}</b>
                    <span>
                      {String(row.owner)} · {String(row.scope)} · updated {String(row.updated)}
                    </span>
                  </span>
                  <Status>{String(row.status)}</Status>
                  <Icon name="arrow" size={14} />
                </button>
              ))}
            </div>
          </Card>
        </div>
        <div className="v2-stack">
          <Card title="Institution health domains" subtitle="Open a specialist workspace for action.">
            <div className="health-domain-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
              {spec.metrics.map((m, i) => (
                <button
                  type="button"
                  className="health-domain"
                  key={m.label}
                  onClick={() =>
                    onNavigate(
                      ["analytics", "success", "quality", "resources"][i % 4],
                      [
                        "executive-analytics",
                        "student-success-dashboard",
                        "accreditation",
                        "funding-budgets",
                      ][i % 4],
                    )
                  }
                >
                  <span>{m.label}</span>
                  <strong>{m.value}</strong>
                  <small>{m.trend}</small>
                </button>
              ))}
            </div>
          </Card>
          <Card title="Governance notes">
            <Notes spec={spec} />
          </Card>
        </div>
      </div>
    </>
  );
}

function QueueLayout({
  spec,
  rows,
  onOpen,
  onMove,
  canManage,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
  onMove: (id: string, status: string) => void;
  canManage: boolean;
}) {
  const academic = spec.id === "curriculum-approvals";
  const academicGroups: Record<string, string> = {
    draft: "Draft",
    in_review: "In review",
    approved: "Approved",
  };
  const groups = academic
    ? ["Draft", "In review", "Approved", "History"]
    : ["Urgent", "Review due", "On track", "Ready"];
  return (
    <div className="kanban-v2">
      {groups.map(group => {
        const items = rows.filter((r, i) =>
          academic
            ? (academicGroups[String(r.status)] || "History") === group
            : String(r.status) === group || i % groups.length === groups.indexOf(group),
        );
        return (
          <section className="kanban-column" key={group}>
            <div className="kanban-head">
              <b>{group}</b>
              <span>{items.length}</span>
            </div>
            {items.map(row => (
              <article className="kanban-card" key={String(row.id)}>
                <b>{String(row.name)}</b>
                <p>
                  {String(row.owner)} · {String(row.scope)}
                </p>
                <Status>{String(row.status)}</Status>
                <div className="kanban-card-actions">
                  <button type="button" onClick={() => onOpen(row)}>
                    Open
                  </button>
                  <button
                    type="button"
                    disabled={
                      academic &&
                      (!canManage || !["draft", "in_review", "approved"].includes(String(row.status)))
                    }
                    title={
                      academic
                        ? "Record the next governed action for this exact curriculum version."
                        : undefined
                    }
                    onClick={() => onMove(String(row.id), group === "Ready" ? "Active" : "Ready")}
                  >
                    {group === "Ready" ? "Reopen" : "Advance"}
                  </button>
                </div>
              </article>
            ))}
          </section>
        );
      })}
    </div>
  );
}

function ScorecardLayout({
  spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  return (
    <div className="v2-grid">
      <div className="v2-stack">
        <Card
          title={`${spec.title} trend`}
          subtitle="Privacy-thresholded, explainable institutional measure."
        >
          <MiniChart values={spec.chart} />
        </Card>
        <Card
          title="Domain drill-down"
          subtitle="Select a row to inspect measure definition, owner, source and actions."
        >
          <RecordsTable spec={spec} rows={rows} onOpen={onOpen} />
        </Card>
      </div>
      <div className="v2-stack">
        <Card title="Current domains">
          <div className="health-domain-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
            {spec.metrics.map(m => (
              <div className="health-domain" key={m.label}>
                <span>{m.label}</span>
                <strong>{m.value}</strong>
                <small>{m.trend}</small>
              </div>
            ))}
          </div>
        </Card>
        <Card title="Measure governance">
          <Notes spec={spec} />
        </Card>
      </div>
    </div>
  );
}

function CalendarLayout({
  spec: _spec,
  rows,
  onOpen,
  onAdd,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
  onAdd: () => void;
}) {
  const [offset, setOffset] = useState(0);
  const base = new Date(2026, 7 + offset, 1);
  const title = new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric" }).format(base);
  const first = base.getDay();
  const days = new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate();
  const prev = new Date(base.getFullYear(), base.getMonth(), 0).getDate();
  const cells = Array.from({ length: 42 }, (_, i) => {
    const d = i - first + 1;
    return d < 1
      ? { day: prev + d, muted: true }
      : d > days
        ? { day: d - days, muted: true }
        : { day: d, muted: false };
  });
  return (
    <div className="calendar-v2">
      <section>
        <div className="month-toolbar">
          <Button small icon="arrow" onClick={() => setOffset(v => v - 1)}>
            Previous
          </Button>
          <strong>{title}</strong>
          <Button small onClick={() => setOffset(0)}>
            Today
          </Button>
          <Button small icon="arrow" onClick={() => setOffset(v => v + 1)}>
            Next
          </Button>
          <Button small icon="plus" variant="primary" onClick={onAdd}>
            Add
          </Button>
        </div>
        <div className="month-grid-v2">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => (
            <div className="month-cell head" key={d}>
              {d}
            </div>
          ))}
          {cells.map((cell, i) => (
            <div
              className={`month-cell ${cell.muted ? "muted" : ""} ${!cell.muted && cell.day === 25 ? "today" : ""}`}
              key={`${cell.day}-${i}`}
            >
              <span className="month-day">{cell.day}</span>
              {!cell.muted
                ? rows
                    .filter((_, idx) => (idx * 5 + 3) % days === cell.day % days)
                    .slice(0, 2)
                    .map(row => (
                      <button
                        type="button"
                        className="month-event"
                        key={String(row.id)}
                        onClick={() => onOpen(row)}
                      >
                        {String(row.name)}
                      </button>
                    ))
                : null}
            </div>
          ))}
        </div>
      </section>
      <Card title="Upcoming institutional items" subtitle={`${rows.length} connected items`}>
        <div className="timeline">
          {rows.map(row => (
            <button
              type="button"
              className="timeline-item"
              key={String(row.id)}
              onClick={() => onOpen(row)}
              style={{
                width: "100%",
                textAlign: "left",
                color: "inherit",
                background: "transparent",
                border: 0,
              }}
            >
              <b>{String(row.name)}</b>
              <p>
                {String(row.date)} · {String(row.time)} · {String(row.owner)}
              </p>
              <time>{String(row.status)}</time>
            </button>
          ))}
        </div>
      </Card>
    </div>
  );
}

function FormLayout({
  spec,
  values,
  setValues,
  onSave,
  onHistory,
  canManage,
}: {
  spec: WorkspaceSpec;
  values: Record<string, string>;
  setValues: (v: Record<string, string>) => void;
  onSave: () => void;
  onHistory?: () => void;
  canManage: boolean;
}) {
  const liveTenancy = spec.id === "settings-profile" || spec.id === "institution-profile";
  const mid = Math.ceil(spec.fields.length / 2);
  const groups = [spec.fields.slice(0, mid), spec.fields.slice(mid)];
  return (
    <div className="form-layout-v2">
      <div className="form-sections">
        {spec.id === "settings-security" && (
          <section className="form-section-v2">
            <IdentitySecurityControls />
          </section>
        )}
        {groups.map((fields, i) => (
          <section className="form-section-v2" key={i}>
            <h3>{i === 0 ? "Primary configuration" : "Governance and defaults"}</h3>
            <div className="form-fields-v2">
              {fields.map(field => (
                <div
                  className={`form-field-v2 ${field.toLowerCase().includes("notes") || field.toLowerCase().includes("policy") ? "full" : ""}`}
                  key={field}
                >
                  <label>{field}</label>
                  {field.toLowerCase().includes("notes") || field.toLowerCase().includes("policy") ? (
                    <textarea
                      value={values[field] || ""}
                      onChange={e => setValues({ ...values, [field]: e.target.value })}
                      disabled={
                        !canManage ||
                        (spec.id === "institution-profile" && field === "Accreditation reference")
                      }
                    />
                  ) : field.toLowerCase().includes("language") || field.toLowerCase().includes("status") ? (
                    <select
                      value={
                        spec.id === "settings-profile" || spec.id === "institution-profile"
                          ? values[field] || ""
                          : values[field] || "Active"
                      }
                      onChange={e => setValues({ ...values, [field]: e.target.value })}
                      disabled={
                        !canManage ||
                        (spec.id === "institution-profile" && field === "Accreditation reference")
                      }
                    >
                      <option value="">Choose a value</option>
                      {spec.id !== "settings-profile" && spec.id !== "institution-profile" && (
                        <option>Active</option>
                      )}
                      <option>English</option>
                      <option>Luganda</option>
                      <option>Kiswahili</option>
                      {spec.id !== "settings-profile" && spec.id !== "institution-profile" && (
                        <>
                          <option>Draft</option>
                          <option>Review required</option>
                        </>
                      )}
                    </select>
                  ) : (
                    <input
                      placeholder={
                        field === "Country and time zone"
                          ? "Country | IANA time zone"
                          : field === "Accreditation reference"
                            ? "Managed through accreditation"
                            : undefined
                      }
                      value={
                        spec.id === "settings-profile" || spec.id === "institution-profile"
                          ? values[field] || ""
                          : values[field] || `${field} value`
                      }
                      onChange={e => setValues({ ...values, [field]: e.target.value })}
                      disabled={
                        !canManage ||
                        (spec.id === "institution-profile" && field === "Accreditation reference")
                      }
                    />
                  )}
                </div>
              ))}
            </div>
          </section>
        ))}
        <div className="hero-actions" style={{ justifyContent: "flex-end" }}>
          <Button icon="history" onClick={onHistory} disabled={liveTenancy && !onHistory}>
            View change history
          </Button>
          <Button icon="check" variant="primary" onClick={onSave} disabled={!canManage}>
            Save governed changes
          </Button>
        </div>
      </div>
      <div className="v2-stack">
        <Card title="Configuration readiness">
          <div className="capacity-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
            <div className="capacity-card">
              <span>Completeness</span>
              <strong>{liveTenancy ? "Not assessed" : "92%"}</strong>
              <div className="capacity-bar">
                <i style={{ width: liveTenancy ? "0%" : "92%" }} />
              </div>
            </div>
            <div className="capacity-card">
              <span>Approval state</span>
              <strong>{liveTenancy ? "Not assessed" : canManage ? "Ready" : "Read-only"}</strong>
              <div className="capacity-bar">
                <i style={{ width: liveTenancy ? "0%" : canManage ? "78%" : "40%" }} />
              </div>
            </div>
          </div>
        </Card>
        <Card title="Change controls">
          <Notes spec={spec} />
        </Card>
      </div>
    </div>
  );
}

function HierarchyLayout({
  spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  const liveTenancy =
    rows.some(row => "parentId" in row) ||
    spec.id === "people-teams" ||
    ["campuses", "schools-faculties", "departments", "academic-units"].includes(spec.id);
  const levels: WorkspaceRow[][] = [];
  if (liveTenancy) {
    const byId = new Map(rows.map(row => [String(row.id), row]));
    for (const row of rows) {
      let parent = String(row.parentId || ""),
        depth = 0;
      const visited = new Set([String(row.id)]);
      while (parent && byId.has(parent) && !visited.has(parent) && depth < 16) {
        visited.add(parent);
        depth++;
        parent = String(byId.get(parent)?.parentId || "");
      }
      (levels[depth] ||= []).push(row);
    }
  }
  return (
    <div className="v2-grid">
      <div className="v2-stack">
        {liveTenancy ? (
          <div className="hierarchy-canvas">
            <div className="hierarchy-level">
              <button type="button" className="hierarchy-node root" disabled>
                <b>{String(rows[0]?.entityName || "No structure records")}</b>
                <span>Institution structure</span>
              </button>
            </div>
            {levels.map((level, index) => (
              <div key={index}>
                <div className="hierarchy-connector" />
                <div className="hierarchy-level">
                  {level.map(row => (
                    <button
                      type="button"
                      className="hierarchy-node"
                      key={String(row.id)}
                      onClick={() => onOpen(row)}
                    >
                      <b>{String(row.name)}</b>
                      <span>
                        {String(row.scope)} · {String(row.people)} people
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="hierarchy-canvas">
            <div className="hierarchy-level">
              <button type="button" className="hierarchy-node root" onClick={() => onOpen(rows[0])}>
                <b>Chatoner University of Applied AI</b>
                <span>Institution root · delegated authority</span>
              </button>
            </div>
            <div className="hierarchy-connector" />
            <div className="hierarchy-level">
              {rows.slice(0, 3).map(row => (
                <button
                  type="button"
                  className="hierarchy-node"
                  key={String(row.id)}
                  onClick={() => onOpen(row)}
                >
                  <b>{String(row.name)}</b>
                  <span>
                    {String(row.owner)} · {String(row.people)} people
                  </span>
                </button>
              ))}
            </div>
            <div className="hierarchy-connector" />
            <div className="hierarchy-level">
              {rows.slice(3).map(row => (
                <button
                  type="button"
                  className="hierarchy-node"
                  key={String(row.id)}
                  onClick={() => onOpen(row)}
                >
                  <b>{String(row.name)}</b>
                  <span>{String(row.scope)}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        <EntityCards spec={spec} rows={rows} onOpen={onOpen} />
      </div>
      <div className="v2-stack">
        <Card title="Delegated administration">
          <Notes spec={spec} />
        </Card>
        <Card title="Structure readiness">
          <div className="capacity-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
            {spec.metrics.map((m, index) => (
              <div className="capacity-card" key={m.label}>
                <span>{m.label}</span>
                <strong>{m.value}</strong>
                <div className="capacity-bar">
                  <i style={{ width: liveTenancy ? "0%" : `${68 + ((index * 7) % 27)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

function DirectoryLayout({
  spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  return (
    <div className="v2-grid">
      <div>
        <RecordsTable spec={spec} rows={rows} onOpen={onOpen} />
      </div>
      <div className="v2-stack">
        <Card title="Role and status summary">
          <div className="health-domain-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
            {spec.metrics.map(m => (
              <div className="health-domain" key={m.label}>
                <span>{m.label}</span>
                <strong>{m.value}</strong>
                <small>{m.trend}</small>
              </div>
            ))}
          </div>
        </Card>
        <Card title="Access and privacy">
          <Notes spec={spec} />
        </Card>
      </div>
    </div>
  );
}

function OnboardingLayout({
  spec,
  rows,
  onOpen,
  onAdvance,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
  onAdvance: (id: string) => void;
}) {
  const stages = ["Invited", "Identity", "Role setup", "Workspace ready", "Activated"];
  return (
    <>
      <div className="onboarding-pipeline">
        {stages.map((stage, si) => (
          <section className="pipeline-column" key={stage}>
            <div className="pipeline-head">
              <b>{stage}</b>
              <span>{rows.filter((_, i) => i % stages.length === si).length}</span>
            </div>
            {rows
              .filter((_, i) => i % stages.length === si)
              .map(row => (
                <article className="pipeline-card" key={String(row.id)} onClick={() => onOpen(row)}>
                  <b>{String(row.name)}</b>
                  <span>
                    {String(row.owner)} · {String(row.scope)}
                  </span>
                  {Number.isFinite(Number(row.progress)) ? (
                    <Progress value={Number(row.progress)} green />
                  ) : (
                    <span>Not assessed</span>
                  )}
                  <div style={{ display: "flex", gap: 5, marginTop: 7 }}>
                    <Status>{String(row.status)}</Status>
                    {si < stages.length - 1 ? (
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={e => {
                          e.stopPropagation();
                          onAdvance(String(row.id));
                        }}
                      >
                        Advance
                      </button>
                    ) : null}
                  </div>
                </article>
              ))}
          </section>
        ))}
      </div>
      <Card
        title="Onboarding policy"
        subtitle="Roles are activated only after identity, authority, scope and required acknowledgements are verified."
      >
        <Notes spec={spec} />
      </Card>
    </>
  );
}

function MatrixLayout({
  spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  const academic = spec.id === "curriculum-maps";
  const headers = academic
    ? spec.columns.slice(1).map(column => column.label)
    : ["View", "Create", "Edit", "Approve", "Export", "Emergency"];
  return (
    <div className="v2-grid">
      <div className="matrix-wrap">
        <table className="matrix-table">
          <thead>
            <tr>
              <th>{spec.columns[0]?.label || "Item"}</th>
              {headers.map(h => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={String(row.id)} onClick={() => onOpen(row)}>
                <td>
                  <b>{String(row.name)}</b>
                  <div style={{ color: "var(--muted)", fontSize: 14, marginTop: 3 }}>{String(row.scope)}</div>
                </td>
                {headers.map((h, ci) => {
                  if (academic)
                    return <td key={h}>{String(row[spec.columns[ci + 1].key] ?? "Not recorded")}</td>;
                  if (Object.prototype.hasOwnProperty.call(row, "permissions"))
                    return (
                      <td key={h} title="Open the role for its exact assigned permissions">
                        —
                      </td>
                    );
                  const kind = (ri + ci) % 5;
                  return (
                    <td className="matrix-check" key={h}>
                      <span className={`matrix-dot ${kind === 3 ? "gap" : kind === 4 ? "none" : ""}`}>
                        <Icon name={kind === 3 ? "alert" : kind === 4 ? "close" : "check"} size={12} />
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="v2-stack">
        <Card title="Coverage summary">
          <div className="capacity-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
            {spec.metrics.map(m => (
              <div className="capacity-card" key={m.label}>
                <span>{m.label}</span>
                <strong>{m.value}</strong>
                {!academic && (
                  <div className="capacity-bar">
                    <i style={{ width: `${metricPercent(m.value, 76)}%` }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
        <Card title="Matrix controls">
          <Notes spec={spec} />
        </Card>
      </div>
    </div>
  );
}

function ImportLayout({
  spec,
  rows,
  onOpen,
  onRun,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
  onRun: () => void;
}) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState("");
  return (
    <div className="v2-stack">
      <div className="import-stepper">
        {["Select source", "Map fields", "Validate", "Approve & import"].map((label, i) => (
          <button
            type="button"
            disabled={i + 1 > step}
            className={`import-step ${step === i + 1 ? "active" : ""} ${step > i + 1 ? "done" : ""}`}
            key={label}
            onClick={() => setStep(i + 1)}
          >
            <span>Step {i + 1}</span>
            <b>{label}</b>
          </button>
        ))}
      </div>
      <div className="v2-grid equal">
        <label className="drop-zone">
          <input
            type="file"
            accept=".csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            style={{ display: "none" }}
            onChange={e => {
              setFile(e.target.files?.[0]?.name || "");
              setStep(1);
            }}
          />
          <span className="drop-icon">
            <Icon name="upload" size={22} />
          </span>
          <b>{file || "Choose an institution data file"}</b>
          <span>CSV, XLSX or signed API manifest · validation occurs before any record is changed.</span>
        </label>
        <Card title="Current validation" subtitle={`Step ${step} of 4`}>
          <div className="policy-list">
            <div className="policy-item">
              <Icon name="check" />
              <span>
                <strong>Schema detected</strong>
                <span>Institution identifiers and required fields mapped</span>
              </span>
            </div>
            <div className="policy-item">
              <Icon name="alert" />
              <span>
                <strong>3 potential duplicates</strong>
                <span>Existing identities require human review</span>
              </span>
            </div>
            <div className="policy-item">
              <Icon name="shield" />
              <span>
                <strong>No destructive overwrite</strong>
                <span>Production imports are idempotent and audited</span>
              </span>
            </div>
          </div>
          <div className="hero-actions">
            <Button
              icon="arrow"
              variant="primary"
              disabled={!file}
              onClick={() => {
                setStep(v => Math.min(4, v + 1));
                if (step === 4) onRun();
              }}
            >
              {step === 4 ? "Run governed import" : "Continue"}
            </Button>
            {!file ? <span className="import-requirement">Choose a data file to continue.</span> : null}
          </div>
        </Card>
      </div>
      <RecordsTable spec={spec} rows={rows} onOpen={onOpen} />
    </div>
  );
}

function PortfolioLayout({
  spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  return (
    <div className="v2-stack">
      <EntityCards spec={spec} rows={rows} onOpen={onOpen} />
      <div className="v2-grid equal">
        <Card title="Portfolio progress">
          <MiniChart values={spec.chart} />
        </Card>
        <Card title="Governance and next actions">
          <Notes spec={spec} />
        </Card>
      </div>
    </div>
  );
}

function MapLayout({
  spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  return (
    <div className="v2-stack">
      <div className="path-canvas">
        {rows.map((row, i) => (
          <React.Fragment key={String(row.id)}>
            <button
              type="button"
              className={`path-node ${i === 1 ? "active" : ""}`}
              onClick={() => onOpen(row)}
            >
              <b>{String(row.name)}</b>
              <span>
                {String(row.scope)} · {String(row.evidence)} coverage
              </span>
            </button>
            {i < rows.length - 1 ? <Icon name="arrow" className="path-arrow" /> : null}
          </React.Fragment>
        ))}
      </div>
      <div className="v2-grid equal">
        <Card title="Mapping and decision table">
          <RecordsTable spec={spec} rows={rows} onOpen={onOpen} />
        </Card>
        <Card title="Route rules">
          <Notes spec={spec} />
        </Card>
      </div>
    </div>
  );
}

function TableLayout({
  spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  return (
    <div className="v2-grid">
      <RecordsTable spec={spec} rows={rows} onOpen={onOpen} />
      <div className="v2-stack">
        <Card title="Record readiness">
          <div className="capacity-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
            {spec.metrics.map(m => (
              <div className="capacity-card" key={m.label}>
                <span>{m.label}</span>
                <strong>{m.value}</strong>
                <div className="capacity-bar">
                  <i
                    style={{
                      width: spec.id === "course-catalogue" ? "0%" : `${metricPercent(m.value, 72)}%`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </Card>
        <Card title="Official controls">
          <Notes spec={spec} />
        </Card>
      </div>
    </div>
  );
}

function TimetableLayout({
  spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  const times = ["08:00", "10:00", "12:00", "14:00", "16:00"];
  const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
  return (
    <div className="v2-stack">
      <div className="timetable-shell">
        <div className="timetable-grid">
          <div className="time-cell head">Time</div>
          {days.map(d => (
            <div className="time-cell head" key={d}>
              {d}
            </div>
          ))}
          {times.map((time, ti) => (
            <React.Fragment key={time}>
              <div className="time-cell label">{time}</div>
              {days.map((day, di) => {
                const row = rows[(ti + di) % rows.length];
                return (
                  <div className="time-cell" key={day}>
                    <button
                      type="button"
                      className={`schedule-block ${(ti + di) % 2 ? "alt" : ""}`}
                      onClick={() => onOpen(row)}
                    >
                      <b>{String(row.name)}</b>
                      <span>
                        {String(row.owner)} · {String(row.scope)}
                      </span>
                    </button>
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
      <div className="v2-grid equal">
        <Card title="Conflict and capacity checks">
          <Notes spec={spec} />
        </Card>
        <Card title="Scheduled items">
          <RecordsTable spec={spec} rows={rows} onOpen={onOpen} />
        </Card>
      </div>
    </div>
  );
}

function LibraryLayout({
  spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  return (
    <div className="v2-stack">
      <div className="v2-card-grid four">
        {rows.map((row, i) => (
          <button type="button" className="entity-card-v2" key={String(row.id)} onClick={() => onOpen(row)}>
            <div className="entity-card-top">
              <span className="entity-card-icon">
                <Icon name={["book", "file", "video", "database"][i % 4]} />
              </span>
              <span className="entity-card-copy">
                <b>{String(row.name)}</b>
                <span>
                  {String(row.category)} · {String(row.scope)}
                </span>
              </span>
            </div>
            <div className="entity-card-meta">
              <div>
                <span>Accessibility</span>
                <b>{String(row.evidence)}</b>
              </div>
              <div>
                <span>Owner</span>
                <b>{String(row.owner)}</b>
              </div>
            </div>
            <div className="entity-card-foot">
              <Status>{String(row.status)}</Status>
              <span className="toolbar-spacer" />
              <Icon name="arrow" size={14} />
            </div>
          </button>
        ))}
      </div>
      <Card title="Resource governance">
        <Notes spec={spec} />
      </Card>
    </div>
  );
}

function ConnectionLayout({
  spec,
  rows,
  onOpen,
  onTest,
  onHandoff,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
  onTest: () => void;
  onHandoff: () => void;
}) {
  return (
    <div className="v2-stack">
      <section className="connector-hero">
        <div className="connector-copy">
          <div className="page-kicker">Independent service connection</div>
          <h3>{spec.title}</h3>
          <p>{spec.description}</p>
          <span className="connector-domain">
            <Icon name="external" size={13} />
            {spec.external?.domain || "Institution-approved connector"} ·{" "}
            {spec.external?.mode || "governed integration"}
          </span>
          <div className="hero-actions">
            <Button icon="pulse" variant="primary" onClick={onTest}>
              Test connection
            </Button>
            <Button icon="external" variant="green" onClick={onHandoff}>
              Open authorized workspace
            </Button>
          </div>
        </div>
        <div className="connector-status">
          <div>
            <span>Connection status</span>
            <b>Connected · 99.98%</b>
          </div>
          <div>
            <span>Last signed handoff</span>
            <b>8 minutes ago</b>
          </div>
          <div>
            <span>Data scope</span>
            <b>Minimum necessary</b>
          </div>
          <div>
            <span>Named owner</span>
            <b>Digital Learning / IT</b>
          </div>
        </div>
      </section>
      <div className="handoff-flow">
        {[
          ["01", "Institution", "Identity, role and entitlement"],
          ["02", "Signed handoff", "Audience-bound one-time token"],
          ["03", "Independent service", "Own authorization and policy"],
          ["04", "Safe return", "Approved summary or evidence"],
        ].map(item => (
          <div className="handoff-step" key={item[0]}>
            <span>{item[0]}</span>
            <b>{item[1]}</b>
            <p>{item[2]}</p>
          </div>
        ))}
      </div>
      <div className="v2-grid">
        <RecordsTable spec={spec} rows={rows} onOpen={onOpen} />
        <Card title="Trust boundaries">
          <Notes spec={spec} />
        </Card>
      </div>
    </div>
  );
}

function SessionsLayout({
  spec,
  rows,
  onOpen,
  onHandoff,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
  onHandoff: () => void;
}) {
  return (
    <div className="v2-stack">
      <div className="v2-card-grid two">
        {rows.map(row => (
          <article className="entity-card-v2" key={String(row.id)}>
            <div className="entity-card-top">
              <span className="entity-card-icon">
                <Icon name="video" />
              </span>
              <span className="entity-card-copy">
                <b>{String(row.name)}</b>
                <span>
                  {String(row.date)} · {String(row.time)}
                </span>
              </span>
              <Status>{String(row.status)}</Status>
            </div>
            <div className="entity-card-meta">
              <div>
                <span>Expected</span>
                <b>{String(row.people)}</b>
              </div>
              <div>
                <span>Instructor / owner</span>
                <b>{String(row.owner)}</b>
              </div>
            </div>
            <div className="entity-card-foot">
              <Button small icon="eye" onClick={() => onOpen(row)}>
                Prepare
              </Button>
              <Button small icon="external" variant="green" onClick={onHandoff}>
                Enter Class
              </Button>
            </div>
          </article>
        ))}
      </div>
      <Card title="Live-class readiness">
        <Notes spec={spec} />
      </Card>
    </div>
  );
}

function PolicyLayout({
  spec,
  rows,
  toggles,
  setToggles,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  toggles: Record<string, boolean>;
  setToggles: (v: Record<string, boolean>) => void;
  onOpen: (row: WorkspaceRow) => void;
}) {
  return (
    <div className="v2-grid">
      <div className="policy-grid">
        {rows.map((row, i) => (
          <article className="policy-card-v2" key={String(row.id)}>
            <div className="policy-title">
              <span>
                <Icon name={i % 2 ? "shield" : "file"} />
              </span>
              <b>{String(row.name)}</b>
              <span className="toolbar-spacer" />
              <Status>{String(row.status)}</Status>
            </div>
            <p>
              {String(row.scope)} · owner {String(row.owner)} · next review {String(row.date)}
            </p>
            <div className="toggle-row-v2">
              <span>Policy enabled and enforced</span>
              <button
                type="button"
                className={`toggle-v2 ${toggles[String(row.id)] !== false ? "on" : ""}`}
                onClick={() =>
                  setToggles({ ...toggles, [String(row.id)]: toggles[String(row.id)] === false })
                }
              />
            </div>
            <div className="entity-card-foot">
              <Button small icon="eye" onClick={() => onOpen(row)}>
                Open version
              </Button>
              <Button small icon="history">
                History
              </Button>
            </div>
          </article>
        ))}
      </div>
      <div className="v2-stack">
        <Card title="Control posture">
          <div className="capacity-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
            {spec.metrics.map(m => (
              <div className="capacity-card" key={m.label}>
                <span>{m.label}</span>
                <strong>{m.value}</strong>
                <div className="capacity-bar">
                  <i style={{ width: `${metricPercent(m.value, 78)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Card>
        <Card title="Policy authority">
          <Notes spec={spec} />
        </Card>
      </div>
    </div>
  );
}

function CapacityLayout({
  spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  return (
    <div className="v2-stack">
      <div className="capacity-grid">
        {spec.metrics.map((m, i) => (
          <div className="capacity-card" key={m.label}>
            <span>{m.label}</span>
            <strong>{m.value}</strong>
            <div className="capacity-bar">
              <i style={{ width: `${60 + i * 9}%` }} />
            </div>
          </div>
        ))}
      </div>
      <div className="v2-grid">
        <RecordsTable spec={spec} rows={rows} onOpen={onOpen} />
        <Card title="Capacity controls">
          <Notes spec={spec} />
        </Card>
      </div>
    </div>
  );
}

function EvidenceLayout({
  spec,
  rows,
  onOpen,
  onDecision,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
  onDecision: (id: string, status: string) => void;
}) {
  const lanes = ["Pending", "Verified", "Needs information"];
  return (
    <div className="v2-stack">
      <div className="evidence-lanes">
        {lanes.map((lane, li) => (
          <section className="evidence-lane" key={lane}>
            <h4>
              <Icon name={li === 1 ? "check" : "award"} size={14} />
              {lane}
            </h4>
            {rows
              .filter((_, i) => i % lanes.length === li)
              .map(row => (
                <article className="evidence-item-v2" key={String(row.id)} onClick={() => onOpen(row)}>
                  <b>{String(row.name)}</b>
                  <span>
                    {String(row.owner)} · {String(row.evidence)} complete
                  </span>
                  <div className="entity-card-foot">
                    <Status>{String(row.status)}</Status>
                    {li === 0 ? (
                      <Button small icon="check" onClick={() => onDecision(String(row.id), "Verified")}>
                        Verify
                      </Button>
                    ) : null}
                  </div>
                </article>
              ))}
          </section>
        ))}
      </div>
      <div className="v2-grid equal">
        <Card title="Evidence coverage">
          <MiniChart values={spec.chart} />
        </Card>
        <Card title="Decision authority">
          <Notes spec={spec} />
        </Card>
      </div>
    </div>
  );
}

function AnalyticsLayout({ spec, onGenerate }: { spec: WorkspaceSpec; onGenerate: () => void }) {
  const [measures, setMeasures] = useState<string[]>([spec.metrics[0].label, spec.metrics[1].label]);
  const [threshold, setThreshold] = useState("10");
  const toggle = (m: string) => setMeasures(v => (v.includes(m) ? v.filter(x => x !== m) : [...v, m]));
  return (
    <div className="v2-grid">
      <div className="v2-stack">
        <Card
          title="Report definition"
          subtitle="Select explainable measures, audience and privacy controls."
        >
          <div className="form-fields-v2">
            {spec.metrics.map(m => (
              <label className="policy-card-v2" key={m.label} style={{ cursor: "pointer" }}>
                <div className="policy-title">
                  <span>
                    <Icon name={m.icon} />
                  </span>
                  <b>{m.label}</b>
                  <input
                    type="checkbox"
                    checked={measures.includes(m.label)}
                    onChange={() => toggle(m.label)}
                    style={{ marginLeft: "auto" }}
                  />
                </div>
                <p>{m.trend} · source ownership and definitions will be attached.</p>
              </label>
            ))}
            <div className="form-field-v2">
              <label>Minimum reportable group</label>
              <select value={threshold} onChange={e => setThreshold(e.target.value)}>
                <option>5</option>
                <option>10</option>
                <option>15</option>
                <option>20</option>
              </select>
            </div>
            <div className="form-field-v2">
              <label>Audience</label>
              <select>
                <option>University Senate</option>
                <option>Dean and Heads</option>
                <option>Quality Directorate</option>
                <option>Registrar</option>
              </select>
            </div>
          </div>
          <div className="hero-actions">
            <Button icon="chart" variant="primary" onClick={onGenerate}>
              Generate governed preview
            </Button>
          </div>
        </Card>
      </div>
      <div className="v2-stack">
        <Card title="Live preview" subtitle={`${measures.length} measures · minimum group ${threshold}`}>
          <MiniChart values={spec.chart} />
        </Card>
        <Card title="Export controls">
          <Notes spec={spec} />
        </Card>
      </div>
    </div>
  );
}

function BoardLayout({
  spec: _spec,
  rows,
  onOpen,
  onMove,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
  onMove: (id: string, status: string) => void;
}) {
  const stages = ["Draft", "Active", "Review due", "Ready"];
  return (
    <div className="kanban-v2">
      {stages.map((stage, si) => (
        <section className="kanban-column" key={stage}>
          <div className="kanban-head">
            <b>{stage}</b>
            <span>{rows.filter((_, i) => i % stages.length === si).length}</span>
          </div>
          {rows
            .filter((_, i) => i % stages.length === si)
            .map(row => (
              <article className="kanban-card" key={String(row.id)}>
                <b>{String(row.name)}</b>
                <p>
                  {String(row.owner)} · {String(row.scope)}
                </p>
                {Number.isFinite(Number(row.progress)) ? (
                  <Progress value={Number(row.progress)} green />
                ) : (
                  <span>Not assessed</span>
                )}
                <div className="kanban-card-actions">
                  <button type="button" onClick={() => onOpen(row)}>
                    Open
                  </button>
                  <button
                    type="button"
                    onClick={() => onMove(String(row.id), stage === "Ready" ? "Active" : "Ready")}
                  >
                    {stage === "Ready" ? "Reopen" : "Advance"}
                  </button>
                </div>
              </article>
            ))}
        </section>
      ))}
    </div>
  );
}

function RubricLayout({
  spec,
  rows,
  onSave,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onSave: () => void;
}) {
  const levels = ["Exemplary", "Proficient", "Developing", "Insufficient"];
  const [values, setValues] = useState<Record<string, string>>({});
  return (
    <div className="v2-stack">
      <div className="workspace-toolbar">
        <span className="view-chip">
          <Icon name="target" size={13} />
          <strong>{rows.length}</strong> criteria
        </span>
        <span className="view-chip">
          <Icon name="link" size={13} />
          Outcome aligned
        </span>
        <span className="toolbar-spacer" />
        <Button icon="check" variant="primary" onClick={onSave}>
          Save rubric version
        </Button>
      </div>
      <div className="rubric-editor">
        <div className="rubric-row">
          <div className="rubric-cell head">Criterion</div>
          {levels.map(l => (
            <div className="rubric-cell head" key={l}>
              {l}
            </div>
          ))}
        </div>
        {rows.map(row => (
          <div className="rubric-row" key={String(row.id)}>
            <div className="rubric-cell">
              <b>{String(row.name)}</b>
              <div style={{ color: "var(--muted)", fontSize: 14, marginTop: 5 }}>{String(row.scope)}</div>
            </div>
            {levels.map(level => (
              <div className="rubric-cell" key={level}>
                <textarea
                  value={
                    values[`${row.id}-${level}`] ||
                    `${level} evidence descriptor for ${String(row.name).toLowerCase()}.`
                  }
                  onChange={e => setValues({ ...values, [`${row.id}-${level}`]: e.target.value })}
                />
              </div>
            ))}
          </div>
        ))}
      </div>
      <Card title="Calibration and version control">
        <Notes spec={spec} />
      </Card>
    </div>
  );
}

function MediaLayout({
  spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  return (
    <div className="v2-stack">
      <div className="media-grid-v2">
        {rows.map(row => (
          <article className="media-card-v2" key={String(row.id)}>
            <div className="media-preview">
              <button type="button" className="media-play" onClick={() => onOpen(row)}>
                <Icon name="play" size={20} />
              </button>
            </div>
            <div className="media-card-body">
              <b>{String(row.name)}</b>
              <p>
                {String(row.date)} · {String(row.scope)} · {String(row.evidence)} captions
              </p>
              <div className="media-card-actions">
                <Button small icon="play" onClick={() => onOpen(row)}>
                  Watch
                </Button>
                <Button small icon="file">
                  Transcript
                </Button>
                <Button small icon="download">
                  Download
                </Button>
              </div>
            </div>
          </article>
        ))}
      </div>
      <Card title="Recording, caption and retention controls">
        <Notes spec={spec} />
      </Card>
    </div>
  );
}

function WellbeingLayout({
  spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  return (
    <div className="v2-stack">
      <div className="protected-callout">
        <span className="icon-wrap">
          <Icon name="shield" size={18} />
        </span>
        <div>
          <b>Protected support workspace</b>
          <p>
            Case visibility is purpose-limited and minimum-necessary. Aggregate analytics, sponsors,
            instructors and technical administrators do not automatically receive protected case information.
          </p>
        </div>
      </div>
      <div className="v2-grid">
        <RecordsTable spec={spec} rows={rows} onOpen={onOpen} />
        <div className="v2-stack">
          <Card title="Urgent route">
            <div className="policy-list">
              <div className="policy-item">
                <Icon name="alert" />
                <span>
                  <strong>Immediate danger</strong>
                  <span>Use the institution emergency and safeguarding procedure now.</span>
                </span>
              </div>
              <div className="policy-item">
                <Icon name="users" />
                <span>
                  <strong>Named safeguarding owner</strong>
                  <span>Only authorized responders can open full case information.</span>
                </span>
              </div>
              <div className="policy-item">
                <Icon name="history" />
                <span>
                  <strong>Attributable timeline</strong>
                  <span>Access and decisions are retained for review.</span>
                </span>
              </div>
            </div>
          </Card>
          <Card title="Case controls">
            <Notes spec={spec} />
          </Card>
        </div>
      </div>
    </div>
  );
}

function AdvisingLayout({
  spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  return (
    <div className="v2-grid">
      <div className="v2-stack">
        <EntityCards spec={spec} rows={rows} onOpen={onOpen} />
      </div>
      <div className="v2-stack">
        <Card title="Advisor caseload">
          <div className="capacity-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
            {spec.metrics.map(m => (
              <div className="capacity-card" key={m.label}>
                <span>{m.label}</span>
                <strong>{m.value}</strong>
                <div className="capacity-bar">
                  <i style={{ width: `${metricPercent(m.value, 70)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Card>
        <Card title="Documented next actions">
          <div className="timeline">
            {rows.map(row => (
              <div className="timeline-item" key={String(row.id)}>
                <b>{String(row.name)}</b>
                <p>
                  {String(row.owner)} · next meeting {String(row.date)}
                </p>
                <time>{String(row.status)}</time>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

function AidLayout({
  spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  return (
    <div className="v2-stack">
      <div className="finance-summary">
        {spec.metrics.map(m => (
          <div className="finance-card" key={m.label}>
            <span>{m.label}</span>
            <strong>{m.value}</strong>
            <small>{m.trend}</small>
          </div>
        ))}
      </div>
      <div className="v2-grid">
        <EntityCards spec={spec} rows={rows} onOpen={onOpen} />
        <Card title="Protected finance boundaries">
          <Notes spec={spec} />
        </Card>
      </div>
    </div>
  );
}

function PartnershipLayout({
  spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  return (
    <div className="v2-stack">
      <EntityCards spec={spec} rows={rows} onOpen={onOpen} />
      <Card title="Partnership milestone map">
        <div className="path-canvas">
          {rows.map((row, i) => (
            <React.Fragment key={String(row.id)}>
              <button
                type="button"
                className={`path-node ${i === 0 ? "active" : ""}`}
                onClick={() => onOpen(row)}
              >
                <b>{String(row.name)}</b>
                <span>
                  {String(row.people)} learners · {String(row.date)}
                </span>
              </button>
              {i < rows.length - 1 ? <Icon name="arrow" className="path-arrow" /> : null}
            </React.Fragment>
          ))}
        </div>
      </Card>
    </div>
  );
}

function PublicationsLayout({
  spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  return (
    <div className="v2-stack">
      <div className="v2-card-grid">
        {rows.map((row, i) => (
          <article className="entity-card-v2" key={String(row.id)}>
            <div className="entity-card-top">
              <span className="entity-card-icon">
                <Icon name={i % 2 ? "file" : "book"} />
              </span>
              <span className="entity-card-copy">
                <b>{String(row.name)}</b>
                <span>
                  {String(row.category)} · {String(row.owner)}
                </span>
              </span>
              <Status>{String(row.status)}</Status>
            </div>
            <div className="entity-card-meta">
              <div>
                <span>Repository</span>
                <b>{String(row.decision)}</b>
              </div>
              <div>
                <span>Updated</span>
                <b>{String(row.updated)}</b>
              </div>
            </div>
            <div className="entity-card-foot">
              <Button small icon="eye" onClick={() => onOpen(row)}>
                Inspect
              </Button>
              <Button small icon="external">
                Repository
              </Button>
            </div>
          </article>
        ))}
      </div>
      <Card title="Publication and repository controls">
        <Notes spec={spec} />
      </Card>
    </div>
  );
}

function VerificationLayout({
  spec: _spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  const [code, setCode] = useState(String(rows[0]?.name || ""));
  const [result, setResult] = useState<WorkspaceRow | null>(null);
  const verify = () =>
    setResult(rows.find(r => String(r.name).toLowerCase() === code.trim().toLowerCase()) || rows[0]);
  return (
    <div className="verification-shell">
      <section className="verification-box">
        <div className="page-kicker">Public verification boundary</div>
        <h3>Verify an institution or Academy credential</h3>
        <p>
          Verification confirms issuer, credential, status and permitted public information without exposing
          private learner evidence.
        </p>
        <div className="verify-input">
          <input
            value={code}
            onChange={e => {
              setCode(e.target.value);
              setResult(null);
            }}
            placeholder="Enter verification ID"
          />
          <Button icon="search" variant="primary" onClick={verify}>
            Verify
          </Button>
        </div>
        {result ? (
          <div className="verification-result">
            <b>Valid verification record</b>
            <p>
              {String(result.name)} · {String(result.owner)} · status {String(result.status)} · evidence{" "}
              {String(result.evidence)}.
            </p>
            <div className="hero-actions">
              <Button small icon="eye" onClick={() => onOpen(result)}>
                Open public record
              </Button>
              <Button small icon="share">
                Copy link
              </Button>
            </div>
          </div>
        ) : null}
      </section>
      <Card title="Recent institution verification records">
        <div className="list">
          {rows.map(row => (
            <button
              type="button"
              className="list-row"
              key={String(row.id)}
              onClick={() => {
                setCode(String(row.name));
                setResult(row);
              }}
              style={{ width: "100%", textAlign: "left", color: "inherit" }}
            >
              <span className="list-icon">
                <Icon name="certificate" />
              </span>
              <span className="list-copy">
                <b>{String(row.name)}</b>
                <span>
                  {String(row.owner)} · {String(row.updated)}
                </span>
              </span>
              <Status>{String(row.status)}</Status>
            </button>
          ))}
        </div>
      </Card>
    </div>
  );
}

function FinanceLayout({
  spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  return (
    <div className="v2-stack">
      <div className="finance-summary">
        {spec.metrics.map(m => (
          <div className="finance-card" key={m.label}>
            <span>{m.label}</span>
            <strong>{m.value}</strong>
            <small>{m.trend}</small>
          </div>
        ))}
      </div>
      <div className="v2-grid">
        <RecordsTable spec={spec} rows={rows} onOpen={onOpen} />
        <div className="v2-stack">
          <Card title="Utilisation">
            <MiniChart values={spec.chart} />
          </Card>
          <Card title="Financial controls">
            <Notes spec={spec} />
          </Card>
        </div>
      </div>
    </div>
  );
}

function IntegrationHealthLayout({
  spec,
  rows,
  onOpen,
  onTest,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
  onTest: () => void;
}) {
  return (
    <div className="v2-stack">
      <div className="v2-card-grid">
        {rows.map((row, i) => (
          <article className="entity-card-v2" key={String(row.id)}>
            <div className="entity-card-top">
              <span className="entity-card-icon">
                <Icon name={i % 2 ? "pulse" : "link"} />
              </span>
              <span className="entity-card-copy">
                <b>{String(row.name)}</b>
                <span>
                  {String(row.owner)} · last sync {String(row.updated)}
                </span>
              </span>
              <Status>{i === 5 ? "Attention" : "Healthy"}</Status>
            </div>
            <div className="entity-card-meta">
              <div>
                <span>Health</span>
                <b>{String(row.evidence)}</b>
              </div>
              <div>
                <span>Retries</span>
                <b>{i === 5 ? "12" : "0"}</b>
              </div>
            </div>
            <div className="entity-card-foot">
              <Button small icon="pulse" onClick={onTest}>
                Diagnose
              </Button>
              <Button small icon="eye" onClick={() => onOpen(row)}>
                Details
              </Button>
            </div>
          </article>
        ))}
      </div>
      <div className="v2-grid equal">
        <Card title="Latency and success trend">
          <MiniChart values={spec.chart} />
        </Card>
        <Card title="Operational ownership">
          <Notes spec={spec} />
        </Card>
      </div>
    </div>
  );
}

function ExportLayout({
  spec,
  rows,
  onOpen,
  onCreate,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
  onCreate: () => void;
}) {
  const [purpose, setPurpose] = useState("Accreditation evidence review");
  const [mask, setMask] = useState(true);
  return (
    <div className="v2-grid">
      <div className="v2-stack">
        <Card
          title="Request a purpose-limited export"
          subtitle="Exports require role, purpose, scope, masking, approval and expiry."
        >
          <div className="form-fields-v2">
            <div className="form-field-v2 full">
              <label>Purpose</label>
              <input value={purpose} onChange={e => setPurpose(e.target.value)} />
            </div>
            <div className="form-field-v2">
              <label>Dataset</label>
              <select>
                <option>Accreditation evidence</option>
                <option>Enrolment aggregate</option>
                <option>Student success aggregate</option>
                <option>Integration audit</option>
              </select>
            </div>
            <div className="form-field-v2">
              <label>Expiry</label>
              <select>
                <option>24 hours</option>
                <option>7 days</option>
                <option>30 days</option>
              </select>
            </div>
          </div>
          <div className="toggle-row-v2">
            <span>Mask direct identifiers</span>
            <button
              type="button"
              className={`toggle-v2 ${mask ? "on" : ""}`}
              onClick={() => setMask(v => !v)}
            />
          </div>
          <div className="hero-actions">
            <Button icon="lock" variant="primary" onClick={onCreate}>
              Submit export request
            </Button>
          </div>
        </Card>
        <RecordsTable spec={spec} rows={rows} onOpen={onOpen} />
      </div>
      <Card title="Export control requirements">
        <Notes spec={spec} />
      </Card>
    </div>
  );
}

export function LayoutRenderer({
  spec,
  rows,
  onOpen,
  onPrimary,
  onExport: _onExport,
  onMove,
  onAdvance,
  onDecision,
  onSaveForm,
  formValues,
  setFormValues,
  policyToggles,
  setPolicyToggles,
  onTest,
  onHandoff,
  onNavigate,
  canManage,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
  onPrimary: () => void;
  onExport: () => void;
  onMove: (id: string, status: string) => void;
  onAdvance: (id: string) => void;
  onDecision: (id: string, status: string) => void;
  onSaveForm: () => void;
  formValues: Record<string, string>;
  setFormValues: (v: Record<string, string>) => void;
  policyToggles: Record<string, boolean>;
  setPolicyToggles: (v: Record<string, boolean>) => void;
  onTest: () => void;
  onHandoff: () => void;
  onNavigate: (area: string, page: string) => void;
  canManage: boolean;
}) {
  switch (spec.layout) {
    case "dashboard":
      return <DashboardLayout spec={spec} rows={rows} onOpen={onOpen} onNavigate={onNavigate} />;
    case "queue":
      return <QueueLayout spec={spec} rows={rows} onOpen={onOpen} onMove={onMove} canManage={canManage} />;
    case "scorecard":
      return <ScorecardLayout spec={spec} rows={rows} onOpen={onOpen} />;
    case "calendar":
      return <CalendarLayout spec={spec} rows={rows} onOpen={onOpen} onAdd={onPrimary} />;
    case "form":
      return (
        <FormLayout
          spec={spec}
          values={formValues}
          setValues={setFormValues}
          onSave={onSaveForm}
          onHistory={
            (spec.id === "settings-profile" || spec.id === "institution-profile") && rows[0]
              ? () => onOpen(rows[0])
              : undefined
          }
          canManage={canManage}
        />
      );
    case "hierarchy":
      return <HierarchyLayout spec={spec} rows={rows} onOpen={onOpen} />;
    case "directory":
      return <DirectoryLayout spec={spec} rows={rows} onOpen={onOpen} />;
    case "onboarding":
      return <OnboardingLayout spec={spec} rows={rows} onOpen={onOpen} onAdvance={onAdvance} />;
    case "matrix":
      return <MatrixLayout spec={spec} rows={rows} onOpen={onOpen} />;
    case "import":
      return <ImportLayout spec={spec} rows={rows} onOpen={onOpen} onRun={onPrimary} />;
    case "portfolio":
      return <PortfolioLayout spec={spec} rows={rows} onOpen={onOpen} />;
    case "map":
      return <MapLayout spec={spec} rows={rows} onOpen={onOpen} />;
    case "table":
      return <TableLayout spec={spec} rows={rows} onOpen={onOpen} />;
    case "timetable":
      return <TimetableLayout spec={spec} rows={rows} onOpen={onOpen} />;
    case "library":
      return <LibraryLayout spec={spec} rows={rows} onOpen={onOpen} />;
    case "connection":
      return (
        <ConnectionLayout spec={spec} rows={rows} onOpen={onOpen} onTest={onTest} onHandoff={onHandoff} />
      );
    case "sessions":
      return <SessionsLayout spec={spec} rows={rows} onOpen={onOpen} onHandoff={onHandoff} />;
    case "policy":
      return (
        <PolicyLayout
          spec={spec}
          rows={rows}
          toggles={policyToggles}
          setToggles={setPolicyToggles}
          onOpen={onOpen}
        />
      );
    case "capacity":
      return <CapacityLayout spec={spec} rows={rows} onOpen={onOpen} />;
    case "evidence":
      return <EvidenceLayout spec={spec} rows={rows} onOpen={onOpen} onDecision={onDecision} />;
    case "analytics":
      return <AnalyticsLayout spec={spec} onGenerate={onPrimary} />;
    case "board":
      return <BoardLayout spec={spec} rows={rows} onOpen={onOpen} onMove={onMove} />;
    case "rubric":
      return <RubricLayout spec={spec} rows={rows} onSave={onSaveForm} />;
    case "media":
      return <MediaLayout spec={spec} rows={rows} onOpen={onOpen} />;
    case "wellbeing":
      return <WellbeingLayout spec={spec} rows={rows} onOpen={onOpen} />;
    case "advising":
      return <AdvisingLayout spec={spec} rows={rows} onOpen={onOpen} />;
    case "aid":
      return <AidLayout spec={spec} rows={rows} onOpen={onOpen} />;
    case "partnership":
      return <PartnershipLayout spec={spec} rows={rows} onOpen={onOpen} />;
    case "publications":
      return <PublicationsLayout spec={spec} rows={rows} onOpen={onOpen} />;
    case "verification":
      return <VerificationLayout spec={spec} rows={rows} onOpen={onOpen} />;
    case "finance":
      return <FinanceLayout spec={spec} rows={rows} onOpen={onOpen} />;
    case "integration-health":
      return <IntegrationHealthLayout spec={spec} rows={rows} onOpen={onOpen} onTest={onTest} />;
    case "export":
      return <ExportLayout spec={spec} rows={rows} onOpen={onOpen} onCreate={onPrimary} />;
    default:
      throw new Error(`No distinct institution layout registered for ${spec.id}: ${spec.layout}`);
  }
}
